import * as React from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import LocationOnIcon from '@mui/icons-material/LocationOnSharp';
import HotelIcon from '@mui/icons-material/Hotel';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import {
  Typography,
} from '@mui/material';
import { useApp } from '../../../context/App.context';
import { useAuth } from '../../../context';
import DateRangePicker from '../DateRangePicker';
import toast from 'react-hot-toast';
import { LoginForm } from '../Login';
import { createBookingOnServer } from '../../../utils/createBooking';
import {
  computeBookingTotals,
  nightsBetween,
  defaultPriceUnitFor,
  formatMoney,
  formatUnitPrice,
  toCount,
  DEFAULT_CURRENCY,
} from '../../../utils/pricing';

const Container = styled(Dialog)(({ theme }) => ({
  margin: '0'
}));

/**
 * `serviceProvider` is actually a LISTING document — the prop name is
 * historical. See src/utils/booking.js for the naming fix at the data layer.
 */
export function BookingForm({ open, setOpen, serviceProvider }) {
  const { isLoading } = useApp();
  const { user } = useAuth();
  const [scroll] = React.useState('paper');
  const [dateRange, onChangeDate] = React.useState([new Date(), new Date()]);
  const [adults, setAdults] = React.useState(1);
  const [children, setChildren] = React.useState(0);
  const [infants, setInfants] = React.useState(0);
  const [info, setInfo] = React.useState('');
  const [numberOfDays, setNumberOfDays] = React.useState(0);
  const [showLogin, setShowLogin] = React.useState(false);
  const [pendingBooking, setPendingBooking] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);



  const setDates = (e) => {
    onChangeDate(e);
    setNumberOfDays(nightsBetween(e && e[0], e && e[1]));
  };

  // Number inputs hand back strings. Coercing here keeps `travelers.adults`
  // a number on the document — it was previously written as "3" while
  // children and infants stayed numeric.
  const handleAdults = (e) => setAdults(Math.max(1, toCount(e.target.value)));
  const handleChildren = (e) => setChildren(toCount(e.target.value));
  const handleInfants = (e) => setInfants(toCount(e.target.value));

  // Live quote, derived from the listing — not the hardcoded "$260 / Night"
  // and "Total: $560" this dialog used to display regardless of listing.
  const quote = React.useMemo(() => {
    const nights = nightsBetween(dateRange && dateRange[0], dateRange && dateRange[1]);
    return computeBookingTotals({
      unitCost: Number(serviceProvider?.cost) || 0,
      priceUnit: serviceProvider?.priceUnit || defaultPriceUnitFor(serviceProvider?.type),
      currency: serviceProvider?.currency || DEFAULT_CURRENCY,
      nights,
      travelers: { adults, children, infants },
    });
  }, [serviceProvider, dateRange, adults, children, infants]);

  const submitBooking = async () => {
    if (!user) {
      setPendingBooking(true);
      setShowLogin(true);
      return;
    }

    if (!serviceProvider?.id) {
      toast.error("This listing is unavailable right now. Please try again.");
      return;
    }

    setIsSubmitting(true);
    try {
      // The SERVER writes the booking. `bookings` is `allow create: if false`
      // for every client, because price, availability and capacity cannot be
      // enforced from here — a browser could otherwise book a $600 lodge for
      // $1, or take a room that is already gone.
      //
      // We send intent only. buildBooking() above still produces the QUOTE
      // the traveller sees; it is no longer what gets stored.
      await createBookingOnServer({
        listing: serviceProvider,
        travelers: { adults, children, infants },
        dateRange,
        info,
        user,
      });

      toast.success("Booking submitted successfully! Check your dashboard for updates.");
      setAdults(1);
      setChildren(0);
      setInfants(0);
      setInfo('');
      setPendingBooking(false);
      setOpen(false);
    } catch (error) {
      console.error("Booking error: ", error);
      // createBookingOnServer already turns the callable's codes into
      // sentences — "2026-08-01 is already booked", "the maximum is 6".
      // Those are actionable in a way a house string is not.
      toast.error(error?.message || "Failed to complete booking. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  React.useEffect(() => {
    if (user && pendingBooking) {
      submitBooking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pendingBooking]);

  const handleClose = () => {
    setOpen(false);
  };

  const descriptionElementRef = React.useRef(null);

  React.useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  return (
    <Container
      open={open}
      onClose={handleClose}
      scroll={scroll}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
    >
      {/* <DialogTitle id="scroll-dialog-title">Booking</DialogTitle> */}
      <DialogContent style={{ width: '100%', maxWidth: 500, height: '100%' }} >
        <Typography variant="h4" sx={{ fontSize: '1.3rem', display: 'flex', alignItems: 'start' }} gutterBottom >
          <HotelIcon sx={{ fontSize: '1.5rem', marginRight: '.5rem' }} /> {serviceProvider && serviceProvider.name} | Booking
        </Typography>
        <Typography variant="h4" sx={{ fontSize: '1.3rem', display: 'flex', alignItems: 'start' }} gutterBottom >
          <LocationOnIcon sx={{ fontSize: '1.5rem', marginRight: '.5rem' }} /> {serviceProvider && serviceProvider.location}
        </Typography>
        <Typography variant="h4" sx={{ fontSize: '1.3rem', display: 'flex', alignItems: 'start' }} gutterBottom >
          <PersonIcon sx={{ fontSize: '1.5rem', marginRight: '.5rem' }} />
          {formatUnitPrice(quote.unitCost, quote.currency, quote.priceUnit)}
        </Typography>
        <Box
          sx={{ margin: '16px 0px' }}
        >
          <DateRangePicker title="Check In / Check Out Dates" value={dateRange} onChange={setDates} />
        </Box>

        <Box
          component="form"
          // sx={{
          //   '& > :not(style)': { m: 1, width: '25ch' },
          // }}
          noValidate
          autoComplete="off"
        >
          <TextField id="adults" label="Adults" variant="outlined" value={adults} sx={{ marginRight: '12px', marginTop: '12px' }} type='number' onChange={handleAdults} />
          <TextField id="children" label="Children" variant="outlined" value={children} sx={{ marginRight: '12px', marginTop: '12px' }} type='number' onChange={handleChildren} />
          <TextField id="infants" label="Infants" variant="outlined" value={infants} sx={{ marginRight: '12px', marginTop: '12px' }} type='number' onChange={handleInfants} />
        </Box>
        <Box
          component="form"
          autoComplete="off"
        >
          <TextField
            id="info"
            label="Addiotional Information"
            variant="outlined"
            sx={{ marginRight: '12px', marginTop: '24px', width: '100%' }}
            multiline
            maxRows={8}
            value={info}
            onChange={(e) => setInfo(e.target.value)}
          />
        </Box>
        <Box sx={{ margin: '16px 0' }}>
          <Typography variant="h5" sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'start', margin: 0 }} gutterBottom >
            Guests: {quote.guests}
          </Typography>
          <Typography variant="h5" sx={{ fontSize: '1.1rem', display: numberOfDays ? 'flex' : 'none', alignItems: 'start', margin: 0 }} gutterBottom >
            Duration: {`${quote.nights} ${quote.nights > 1 ? 'nights' : 'night'}`}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', margin: '.5rem 0 0 0' }}>
            {formatMoney(quote.unitCost, quote.currency)} &times; {quote.quantity}
            {quote.guests > quote.billableGuests ? ' (infants travel free)' : ''}
          </Typography>
          <Typography variant="h4" sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'start', margin: '.5rem 0 0 0' }} gutterBottom >
            Total: {formatMoney(quote.totalPaid, quote.currency)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={handleClose} style={{ color: '#000000' }}>Cancel</Button>
        <Button
          onClick={() => submitBooking()}
          className='btn-contain'
          variant="contained" endIcon={(isLoading || isSubmitting) ? <CircularProgress style={{ fontSize: '12px', color: '#FF7656', }} size={10} /> : <SendIcon />}
          style={{ width: '170px', fontSize: '12px' }}
          disabled={isLoading || isSubmitting}
        >
          Submit Booking
        </Button>
      </DialogActions>
      <LoginForm open={showLogin} setOpen={setShowLogin} />
    </Container>
  );
}
